import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { allComics } from "@/mocks/comics";
import { categories } from "@/mocks/categories";
import ComicCard from "@/components/base/ComicCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import type { BreadcrumbItem } from "@/components/feature/Breadcrumb";

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<"latest" | "popular" | "rating">("latest");

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug],
  );

  const filteredComics = useMemo(() => {
    if (!slug || slug === "all") return [...allComics];
    return allComics.filter((c) =>
      c.categories.some((cat) => {
        const catSlug = categories.find((cc) => cc.name === cat)?.slug;
        return catSlug === slug;
      }),
    );
  }, [slug]);

  const sortedComics = useMemo(() => {
    const sorted = [...filteredComics];
    if (sortBy === "latest")
      sorted.sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
    if (sortBy === "popular") sorted.sort((a, b) => b.views - a.views);
    if (sortBy === "rating") sorted.sort((a, b) => b.rating - a.rating);
    return sorted;
  }, [filteredComics, sortBy]);

  const totalPages = Math.ceil(sortedComics.length / ITEMS_PER_PAGE);
  const paginatedComics = sortedComics.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: "首页", path: "/" },
    { label: "漫画分类", path: "/category/all" },
    ...(currentCategory && currentCategory.id !== "all"
      ? [{ label: currentCategory.name }]
      : []),
  ];

  const title =
    slug === "all"
      ? "漫画分类 - 全部漫画 - 土豪漫画网"
      : `${currentCategory?.name || ""}漫画 - 土豪漫画网`;

  return (
    <>
      <main className="pt-20 pb-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {slug === "all" || !currentCategory
                  ? "漫画分类"
                  : currentCategory.name + "漫画"}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {filteredComics.length} 部作品
              </p>
            </div>
            <div className="flex items-center gap-2">
              {(["latest", "popular", "rating"] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setSortBy(s);
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === s
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {s === "latest" ? "最新" : s === "popular" ? "人气" : "评分"}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-8">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={`/category/${cat.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  window.REACT_APP_NAVIGATE(`/category/${cat.slug}`);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  slug === cat.slug
                    ? "bg-primary-500/15 text-primary-500"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {cat.name}
              </a>
            ))}
          </div>

          {paginatedComics.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {paginatedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="small" />
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-1.5 mt-10">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-md text-sm bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-base"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (p) => (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                          p === currentPage
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-100 text-foreground-600 hover:bg-background-200"
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-md text-sm bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-base"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </div>
              <p className="mt-4 text-foreground-500">该分类下暂无漫画</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}